import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Car,
  ClipboardCheck,
  ChevronDown,
  FileSpreadsheet,
  Inbox,
  Plus,
  type LucideIcon,
} from 'lucide-react';
import type { Papel } from '@erp/shared';
import { Button } from '@erp/ui';

interface Opcao {
  para: string;
  rotulo: string;
  icone: LucideIcon;
  papeis: Papel[];
}

const OPCOES: Opcao[] = [
  { para: '/estoque/novo', rotulo: 'Carro', icone: Car, papeis: ['dono', 'gerente'] },
  {
    para: '/estoque/avaliacoes/nova',
    rotulo: 'Avaliação de carro (antes de comprar)',
    icone: ClipboardCheck,
    papeis: ['dono', 'gerente'],
  },
  {
    para: '/leads?novo=1',
    rotulo: 'Lead (cliente interessado)',
    icone: Inbox,
    papeis: ['dono', 'gerente', 'vendedor'],
  },
  {
    para: '/financeiro?aba=contas&tipo=receber&novo=1',
    rotulo: 'Conta a receber',
    icone: ArrowDownCircle,
    papeis: ['dono', 'financeiro'],
  },
  {
    para: '/financeiro?aba=contas&tipo=pagar&novo=1',
    rotulo: 'Conta a pagar',
    icone: ArrowUpCircle,
    papeis: ['dono', 'financeiro'],
  },
  {
    para: '/estoque/importar',
    rotulo: 'Importar estoque de planilha',
    icone: FileSpreadsheet,
    papeis: ['dono', 'gerente'],
  },
];

/** Botão único "Novo" no topo: tudo o que dá para cadastrar, num lugar só. */
export function MenuNovo({ papel }: { papel: Papel }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const opcoes = OPCOES.filter((o) => o.papeis.includes(papel));
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);
  if (opcoes.length === 0) return null;
  return (
    <div ref={ref} className="relative hidden md:block">
      <Button
        size="sm"
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => setAberto((a) => !a)}
      >
        <Plus /> Novo <ChevronDown className="opacity-70" />
      </Button>
      {aberto && (
        <div
          role="menu"
          aria-label="Cadastrar"
          className="absolute right-0 top-full z-30 mt-1 w-64 rounded-lg border bg-card p-1 shadow-lg"
        >
          {opcoes.map((o) => (
            <Link
              key={o.para}
              to={o.para}
              role="menuitem"
              onClick={() => setAberto(false)}
              className="flex items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
            >
              <o.icone className="size-4 text-muted-foreground" aria-hidden />
              {o.rotulo}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
